import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import journeyTracker from '../utils/journeyTracker';
// Profile icons
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SavingsIcon from '@mui/icons-material/Savings';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SpeedIcon from '@mui/icons-material/Speed';
// Feature icons
import AnalyticsIcon from '@mui/icons-material/Analytics';
import CalculateIcon from '@mui/icons-material/Calculate';
import LanguageIcon from '@mui/icons-material/Language';
import GroupsIcon from '@mui/icons-material/Groups';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import EventIcon from '@mui/icons-material/Event';
import BoltIcon from '@mui/icons-material/Bolt';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import ForumIcon from '@mui/icons-material/Forum';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CategoryIcon from '@mui/icons-material/Category';
import StarIcon from '@mui/icons-material/Star';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
// Thesis icons
import ContrastIcon from '@mui/icons-material/Contrast';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import MergeIcon from '@mui/icons-material/Merge';
import CycloneIcon from '@mui/icons-material/Cyclone';
// UI icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import './OnboardingWizard.css';

const TOTAL_STEPS = 7;

// Profile definitions with mapping to lists and market caps
const PROFILES = [
  {
    id: 'blue_chip',
    name: 'Blue Chip Investor',
    description: 'Stable, established companies for long-term holding',
    icon: AccountBalanceIcon,
    marketCaps: ['mega', 'large'],
    lists: ['berkshire_hathaway', 'bellwether_stocks', 'undervalued_large_caps', 'magnificent_seven']
  },
  {
    id: 'growth_seeker',
    name: 'Growth Seeker',
    description: 'High-growth companies with strong fundamentals',
    icon: TrendingUpIcon,
    marketCaps: ['large', 'mid'],
    lists: ['earnings_growth_leaders', 'analyst_favorites', 'attractive_peg', 'revenue_growth_leaders']
  },
  {
    id: 'small_cap_hunter',
    name: 'Small Cap Hunter',
    description: 'Higher risk/reward, emerging opportunities',
    icon: RocketLaunchIcon,
    marketCaps: ['small', 'mid', 'micro'],
    lists: ['aggressive_small_caps', 'small_cap_gainers', 'growth_technology_stocks']
  },
  {
    id: 'dividend_collector',
    name: 'Dividend Collector',
    description: 'Steady income from established payers',
    icon: SavingsIcon,
    marketCaps: ['mega', 'large'],
    lists: ['highest_dividends', 'undervalued_large_caps', 'bellwether_stocks']
  },
  {
    id: 'swing_trader',
    name: 'Swing Trader',
    description: 'Technical patterns, holding days to weeks',
    icon: ShowChartIcon,
    marketCaps: ['mega', 'large', 'mid', 'small', 'micro'],
    lists: ['buy_signals', 'momentum_breakout', 'support_bounce', 'potential_breakout', 'bullish_trend']
  },
  {
    id: 'day_trader',
    name: 'Day Trader',
    description: 'Intraday moves, volume, and volatility',
    icon: SpeedIcon,
    marketCaps: ['mega', 'large', 'mid', 'small', 'micro'],
    lists: ['day_gainers', 'most_actives', 'extended_hours_movers']
  }
];

// Step 2: Analysis Tools
const ANALYSIS_FEATURES = [
  {
    id: 'analyst_ratings',
    name: 'Analyst Ratings',
    description: 'Wall Street price targets & buy/sell recommendations',
    icon: AnalyticsIcon,
    lists: ['analyst_favorites']
  },
  {
    id: 'financial_ratios',
    name: 'Financial Ratios',
    description: 'P/E, PEG, debt ratios, profit margins',
    icon: CalculateIcon,
    lists: ['attractive_peg', 'undervalued_growth_stocks']
  },
  {
    id: 'web_traffic',
    name: 'Web Traffic Trends',
    description: 'Company website visitor growth signals',
    icon: LanguageIcon,
    lists: ['traffic_gainers']
  },
  {
    id: 'employee_outlook',
    name: 'Employee Outlook',
    description: 'Glassdoor sentiment & business outlook',
    icon: GroupsIcon,
    lists: ['high_employee_outlook']
  }
];

// Step 3: Trading Signals
const TRADING_FEATURES = [
  {
    id: 'technical_patterns',
    name: 'Technical Patterns',
    description: 'Golden cross, death cross, double bottoms',
    icon: CandlestickChartIcon,
    lists: ['golden_cross', 'double_bottom', 'potential_breakout']
  },
  {
    id: 'buy_sell_signals',
    name: 'Buy & Sell Signals',
    description: 'Aggregated technical indicators',
    icon: NotificationsActiveIcon,
    lists: ['buy_signals', 'sell_signals', 'oversold_reversal']
  },
  {
    id: 'earnings_events',
    name: 'Earnings Events',
    description: 'Upcoming reports, surprises, post-earnings moves',
    icon: EventIcon,
    lists: ['upcoming_earnings', 'earnings_surprises', 'earnings_today']
  },
  {
    id: 'momentum',
    name: 'Momentum & Trends',
    description: 'Trending stocks, gainers, most active',
    icon: BoltIcon,
    lists: ['trending', 'day_gainers', 'most_actives', 'bullish_trend']
  }
];

// Step 4: Market Pulse
const MARKET_FEATURES = [
  {
    id: 'fear_greed',
    name: 'Fear & Greed Index',
    description: 'Market sentiment gauge from CNN',
    icon: SentimentSatisfiedAltIcon,
    lists: [],
    enablesFeature: 'fear_greed_widget'
  },
  {
    id: 'sector_rotation',
    name: 'Sector Rotation',
    description: 'Money flow between market sectors',
    icon: DonutSmallIcon,
    lists: [],
    enablesFeature: 'sector_rotation_widget'
  },
  {
    id: 'social_sentiment',
    name: 'Social Sentiment',
    description: 'Reddit & WallStreetBets trending stocks',
    icon: ForumIcon,
    lists: ['reddit_trending', 'wsb_favorites', 'meme_stocks']
  },
  {
    id: 'market_overview',
    name: 'Market Overview',
    description: 'Daily AI-powered market analysis dashboard',
    icon: DashboardIcon,
    lists: [],
    enablesNotification: 'market_overview'
  }
];

// Step 5: Themes & AI
const THEMES_FEATURES = [
  {
    id: 'ai_lists',
    name: 'AI-Powered Lists',
    description: 'Create custom screens with natural language',
    icon: AutoAwesomeIcon,
    lists: [],
    enablesFeature: 'ai_filter_highlight'
  },
  {
    id: 'thematic',
    name: 'Thematic Investing',
    description: 'AI, Nuclear, GLP-1, Autonomous Driving',
    icon: CategoryIcon,
    lists: ['ai_beneficiaries', 'nuclear_power_stocks', 'metabolic_regulators_stocks', 'quantum_computing_stocks']
  },
  {
    id: 'curated',
    name: 'Curated Portfolios',
    description: 'Buffett holdings, politician trades, Cramer picks',
    icon: StarIcon,
    lists: ['berkshire_hathaway', 'politician_stocks', 'jim_cramer_recommendations']
  },
  {
    id: 'smart_screens',
    name: 'Smart Screens',
    description: 'Pre-built screens for every strategy',
    icon: PsychologyIcon,
    lists: ['strategic_acquisition_targets', 'index_inclusion_candidates']
  }
];

// Step 6: Investment Thesis (strategy focus)
const THESIS_FEATURES = [
  {
    id: 'short_squeeze',
    name: 'Short Squeeze',
    description: 'High short interest stocks vulnerable to squeezes',
    icon: TrendingUpIcon,
    lists: ['short_squeeze_candidates'],
    color: '#ef4444'
  },
  {
    id: 'contrarian_value',
    name: 'Contrarian Value',
    description: 'Beaten-down stocks with improving fundamentals',
    icon: ContrastIcon,
    lists: ['contrarian_value'],
    color: '#f59e0b'
  },
  {
    id: 'turnaround_stories',
    name: 'Turnaround Stories',
    description: 'Companies recovering from 52-week lows',
    icon: AutorenewIcon,
    lists: ['turnaround_stories'],
    color: '#10b981'
  },
  {
    id: 'ma_targets',
    name: 'M&A Targets',
    description: 'Attractive acquisition candidates',
    icon: MergeIcon,
    lists: ['ma_target_candidates'],
    color: '#8b5cf6'
  },
  {
    id: 'cyclical_recovery',
    name: 'Cyclical Recovery',
    description: 'Cyclical industries poised for rebound',
    icon: CycloneIcon,
    lists: ['cyclical_recovery'],
    color: '#3b82f6'
  },
  {
    id: 'high_beta_disruptors',
    name: 'High Beta Disruptors',
    description: 'High-growth disruptors with strong momentum',
    icon: RocketLaunchIcon,
    lists: ['high_beta_disruptors'],
    color: '#ec4899'
  }
];

const OnboardingWizard = ({
  isOpen,
  onClose,
  onComplete,
  existingPreferences
}) => {
  const [step, setStep] = useState(1);
  // Changed to array for multi-select profiles
  const [selectedProfiles, setSelectedProfiles] = useState(
    existingPreferences?.onboarding?.profiles ||
    (existingPreferences?.onboarding?.profile ? [existingPreferences.onboarding.profile] : [])
  );
  const [selectedFeatures, setSelectedFeatures] = useState(
    existingPreferences?.onboarding?.features || []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track wizard open
  useEffect(() => {
    if (isOpen) {
      journeyTracker.trackAction('onboarding_wizard_open');
    }
  }, [isOpen]);

  const handleProfileToggle = (profileId) => {
    setSelectedProfiles(prev => {
      const newProfiles = prev.includes(profileId)
        ? prev.filter(id => id !== profileId)
        : [...prev, profileId];
      journeyTracker.trackAction('onboarding_profile_toggle', { profile: profileId, selected: !prev.includes(profileId) });
      return newProfiles;
    });
  };

  const handleFeatureToggle = (featureId) => {
    setSelectedFeatures(prev =>
      prev.includes(featureId)
        ? prev.filter(id => id !== featureId)
        : [...prev, featureId]
    );
  };

  const handleNext = () => {
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSkipToEnd = () => {
    setStep(TOTAL_STEPS);
  };

  const handleSkip = () => {
    journeyTracker.trackAction('onboarding_wizard_skip');

    // Set defaults for explorer mode
    const explorerProfile = {
      profiles: ['explorer'],
      features: [],
      completedAt: new Date().toISOString(),
      version: '3.0'
    };

    onComplete({
      onboarding: explorerProfile,
      marketCaps: ['mega', 'large', 'mid', 'small', 'micro'],
      favoriteLists: ['trending', 'in_the_news', 'reddit_trending']
    });
    onClose();
  };

  const handleComplete = async () => {
    if (selectedProfiles.length === 0) return;

    setIsSubmitting(true);

    try {
      // Gather all lists from all selected profiles
      let allLists = [];
      let allMarketCaps = new Set();

      selectedProfiles.forEach(profileId => {
        const profile = PROFILES.find(p => p.id === profileId);
        if (profile) {
          allLists = [...allLists, ...profile.lists];
          profile.marketCaps.forEach(cap => allMarketCaps.add(cap));
        }
      });

      // Add lists from selected features (including thesis)
      const allFeatures = [...ANALYSIS_FEATURES, ...TRADING_FEATURES, ...MARKET_FEATURES, ...THEMES_FEATURES, ...THESIS_FEATURES];
      selectedFeatures.forEach(featureId => {
        const feature = allFeatures.find(f => f.id === featureId);
        if (feature?.lists) {
          allLists = [...allLists, ...feature.lists];
        }
      });

      // Remove duplicates
      allLists = [...new Set(allLists)];

      const onboardingData = {
        profiles: selectedProfiles,
        features: selectedFeatures,
        completedAt: new Date().toISOString(),
        version: '3.0'
      };

      await onComplete({
        onboarding: onboardingData,
        marketCaps: [...allMarketCaps],
        favoriteLists: allLists
      });

      journeyTracker.trackAction('onboarding_wizard_complete', {
        profiles: selectedProfiles,
        features: selectedFeatures,
        lists_count: allLists.length
      });

      onClose();
    } catch (error) {
      console.error('[ONBOARDING] Error completing onboarding:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedProfiles = () => PROFILES.filter(p => selectedProfiles.includes(p.id));

  const renderFeatureCard = (feature, isSelected) => {
    const Icon = feature.icon;
    return (
      <button
        key={feature.id}
        className={`feature-card ${isSelected ? 'selected' : ''}`}
        onClick={() => handleFeatureToggle(feature.id)}
      >
        <div className="feature-card-icon">
          <Icon />
        </div>
        <div className="feature-card-content">
          <h3>{feature.name}</h3>
          <p>{feature.description}</p>
        </div>
        {isSelected && (
          <div className="feature-card-check">
            <CheckCircleIcon />
          </div>
        )}
      </button>
    );
  };

  const renderStep1 = () => (
    <div className="onboarding-step">
      <h2 className="onboarding-title">What best describes your investing style?</h2>
      <p className="onboarding-subtitle">Select all that apply - you can combine multiple approaches</p>

      <div className="profile-grid">
        {PROFILES.map(profile => {
          const Icon = profile.icon;
          const isSelected = selectedProfiles.includes(profile.id);

          return (
            <button
              key={profile.id}
              className={`profile-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleProfileToggle(profile.id)}
            >
              <div className="profile-card-icon">
                <Icon />
              </div>
              <div className="profile-card-content">
                <h3>{profile.name}</h3>
                <p>{profile.description}</p>
              </div>
              {isSelected && (
                <div className="profile-card-check">
                  <CheckCircleIcon />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="onboarding-step">
      <h2 className="onboarding-title">What analysis tools would you like?</h2>
      <p className="onboarding-subtitle">Select any that interest you</p>

      <div className="feature-grid">
        {ANALYSIS_FEATURES.map(feature =>
          renderFeatureCard(feature, selectedFeatures.includes(feature.id))
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="onboarding-step">
      <h2 className="onboarding-title">What trading signals do you follow?</h2>
      <p className="onboarding-subtitle">Technical patterns and momentum indicators</p>

      <div className="feature-grid">
        {TRADING_FEATURES.map(feature =>
          renderFeatureCard(feature, selectedFeatures.includes(feature.id))
        )}
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="onboarding-step">
      <h2 className="onboarding-title">Which market-wide insights matter to you?</h2>
      <p className="onboarding-subtitle">Sentiment, sectors, and macro signals</p>

      <div className="feature-grid">
        {MARKET_FEATURES.map(feature =>
          renderFeatureCard(feature, selectedFeatures.includes(feature.id))
        )}
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="onboarding-step">
      <h2 className="onboarding-title">Interested in themes or AI-powered lists?</h2>
      <p className="onboarding-subtitle">Curated portfolios and smart discovery</p>

      <div className="feature-grid">
        {THEMES_FEATURES.map(feature =>
          renderFeatureCard(feature, selectedFeatures.includes(feature.id))
        )}
      </div>
    </div>
  );

  // Step 6: Investment Thesis strategies
  const renderStep6 = () => (
    <div className="onboarding-step">
      <h2 className="onboarding-title">What investment strategies interest you?</h2>
      <p className="onboarding-subtitle">Select thesis-driven approaches you'd like to explore</p>

      <div className="feature-grid thesis-grid">
        {THESIS_FEATURES.map(feature => {
          const Icon = feature.icon;
          const isSelected = selectedFeatures.includes(feature.id);
          return (
            <button
              key={feature.id}
              className={`feature-card thesis-card ${isSelected ? 'selected' : ''}`}
              onClick={() => handleFeatureToggle(feature.id)}
              style={{ '--thesis-color': feature.color }}
            >
              <div className="feature-card-icon" style={{ color: feature.color }}>
                <Icon />
              </div>
              <div className="feature-card-content">
                <h3>{feature.name}</h3>
                <p>{feature.description}</p>
              </div>
              {isSelected && (
                <div className="feature-card-check">
                  <CheckCircleIcon />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 7: Summary
  const renderStep7 = () => {
    const profiles = getSelectedProfiles();
    const allFeatures = [...ANALYSIS_FEATURES, ...TRADING_FEATURES, ...MARKET_FEATURES, ...THEMES_FEATURES, ...THESIS_FEATURES];
    const selectedFeatureNames = selectedFeatures
      .map(id => allFeatures.find(f => f.id === id)?.name)
      .filter(Boolean);

    // Calculate total lists from profiles
    let totalLists = 0;
    let allMarketCaps = new Set();
    profiles.forEach(profile => {
      totalLists += profile.lists.length;
      profile.marketCaps.forEach(cap => allMarketCaps.add(cap));
    });

    // Add lists from features
    selectedFeatures.forEach(featureId => {
      const feature = allFeatures.find(f => f.id === featureId);
      if (feature?.lists) {
        totalLists += feature.lists.length;
      }
    });

    return (
      <div className="onboarding-step">
        <h2 className="onboarding-title">You're all set!</h2>
        <p className="onboarding-subtitle">Here's what we'll set up for you</p>

        <div className="summary-section">
          <div className="summary-item">
            <h4>Your Investing Styles ({profiles.length})</h4>
            <div className="summary-chips">
              {profiles.map(profile => (
                <span key={profile.id} className="summary-chip profile">
                  <profile.icon style={{ fontSize: 16, marginRight: 4 }} />
                  {profile.name}
                </span>
              ))}
            </div>
          </div>

          <div className="summary-item">
            <h4>Market Cap Focus</h4>
            <div className="summary-chips">
              {[...allMarketCaps].map(cap => (
                <span key={cap} className="summary-chip">
                  {cap.charAt(0).toUpperCase() + cap.slice(1)}
                </span>
              ))}
            </div>
          </div>

          {selectedFeatureNames.length > 0 && (
            <div className="summary-item">
              <h4>Features & Strategies ({selectedFeatureNames.length})</h4>
              <div className="summary-chips">
                {selectedFeatureNames.slice(0, 8).map(name => (
                  <span key={name} className="summary-chip feature">
                    {name}
                  </span>
                ))}
                {selectedFeatureNames.length > 8 && (
                  <span className="summary-chip more">
                    +{selectedFeatureNames.length - 8} more
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="summary-item">
            <h4>Lists Added to Favorites</h4>
            <div className="summary-value">
              <span className="summary-count">{totalLists}</span>
              <span>curated lists ready to explore</span>
            </div>
          </div>
        </div>

        <p className="summary-note">
          You can always change these in your profile settings later.
        </p>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (step) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      case 7: return renderStep7();
      default: return null;
    }
  };

  const getStepLabel = () => {
    switch (step) {
      case 1: return 'Style';
      case 2: return 'Analysis';
      case 3: return 'Signals';
      case 4: return 'Market';
      case 5: return 'Themes';
      case 6: return 'Thesis';
      case 7: return 'Summary';
      default: return '';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      className="onboarding-modal"
      overlayClassName="onboarding-overlay"
      shouldCloseOnOverlayClick={false}
    >
      <button className="onboarding-close" onClick={onClose}>
        <CloseIcon />
      </button>

      <div className="onboarding-progress">
        <div className="progress-dots">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(num => (
            <div
              key={num}
              className={`progress-dot ${step >= num ? 'active' : ''} ${step === num ? 'current' : ''}`}
            />
          ))}
        </div>
        <span className="progress-label">{getStepLabel()}</span>
      </div>

      <div className="onboarding-content">
        {renderStepContent()}
      </div>

      <div className="onboarding-actions">
        <div className="onboarding-actions-left">
          {step === 1 ? (
            <button className="onboarding-skip" onClick={handleSkip}>
              Skip for now
            </button>
          ) : (
            <button className="onboarding-back" onClick={handleBack}>
              <ArrowBackIcon /> Back
            </button>
          )}
        </div>

        <div className="onboarding-actions-right">
          {step > 1 && step < TOTAL_STEPS && (
            <button className="onboarding-skip-to-end" onClick={handleSkipToEnd}>
              Skip to finish
            </button>
          )}

          {step < TOTAL_STEPS ? (
            <button
              className="onboarding-next"
              onClick={handleNext}
              disabled={step === 1 && selectedProfiles.length === 0}
            >
              Next <ArrowForwardIcon />
            </button>
          ) : (
            <button
              className="onboarding-complete"
              onClick={handleComplete}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Setting up...' : 'Get Started'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

OnboardingWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  existingPreferences: PropTypes.shape({
    onboarding: PropTypes.shape({
      profile: PropTypes.string, // Legacy single profile support
      profiles: PropTypes.arrayOf(PropTypes.string), // New multi-select profiles
      features: PropTypes.arrayOf(PropTypes.string)
    })
  })
};

OnboardingWizard.defaultProps = {
  existingPreferences: null
};

// Export definitions for use elsewhere
export { PROFILES, ANALYSIS_FEATURES, TRADING_FEATURES, MARKET_FEATURES, THEMES_FEATURES, THESIS_FEATURES };
export default OnboardingWizard;
